import type { PendingGift } from "@studio/shared";

export class GiftToastQueue {
  readonly #seen = new Set<number>();
  readonly #pending: PendingGift[] = [];

  /** 목록이 바뀐 횟수 — 새 선물이 들어오거나 확인으로 빠질 때만 오른다(같은 선물을 다시 받으면 그대로). */
  #revision = 0;

  revision(): number {
    return this.#revision;
  }

  enqueue(gifts: readonly PendingGift[]): void {
    for (const gift of [...gifts].sort((left, right) => left.seq - right.seq)) {
      if (!Number.isSafeInteger(gift.seq) || gift.seq < 0 || this.#seen.has(gift.seq)) continue;
      this.#seen.add(gift.seq);
      this.#pending.push({ ...gift });
      this.#revision += 1;
    }
    this.#pending.sort((left, right) => left.seq - right.seq);
  }

  current(): PendingGift | null {
    return this.#pending[0] ?? null;
  }

  snapshot(): PendingGift[] {
    return this.#pending.map((gift) => ({ ...gift }));
  }

  async confirmCurrent(acknowledge: (upToSeq: number) => Promise<void>): Promise<void> {
    const gift = this.current();
    if (!gift) return;
    await acknowledge(gift.seq);
    if (this.#pending[0]?.seq === gift.seq) {
      this.#pending.shift();
      this.#revision += 1;
    }
  }
}

const studentQueue = new GiftToastQueue();
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

export function publishPendingGifts(gifts: readonly PendingGift[]): void {
  studentQueue.enqueue(gifts);
  notify();
}

/**
 * 선물 목록의 변경 번호. 화면이 «선물이 새로 왔거나 확인됐는가» 를 알 때 쓴다.
 * 🔑 맨 앞 선물 번호로는 못 안다 — 앞 선물을 확인하기 전에 뒤 선물이 오면 맨 앞은 그대로다(Codex 검수 지적).
 */
export function pendingGiftsRevision(): number {
  return studentQueue.revision();
}

export function currentPendingGift(): PendingGift | null {
  return studentQueue.current();
}

export function subscribePendingGifts(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function confirmPendingGift(
  acknowledge: (upToSeq: number) => Promise<void>,
): Promise<void> {
  await studentQueue.confirmCurrent(acknowledge);
  notify();
}
